/** Shared building blocks for the admin screens. */
import "./admin.css";
import {
  type Dispatch,
  type FormEvent,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import type { OccurrenceStatus } from "../../shared/contracts";
import { addDays, weekdayOf } from "../../shared/time";
import type { AuditEvent, BusinessSettings, Person, PublicConfig, Site } from "../../shared/types";
import { Dialog, Empty, Field } from "../components/ui";
import { ApiError } from "../lib/api";
import { fmtDateTime, isoLocalDate } from "../lib/format";
import { type Loadable, useApi } from "../lib/useApi";

export const useConfig = () => useApi<PublicConfig>("/config");
export const usePeople = () => useApi<Person[]>("/admin/people");
export const useSites = () => useApi<Site[]>("/admin/sites");
export const useSettings = () => useApi<BusinessSettings>("/admin/settings");

/** Normalises anything thrown into an ApiError so ErrorBanner can show it. */
export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  return new ApiError(0, "error", e instanceof Error ? e.message : String(e));
}

/** True when an endpoint simply isn't there (not deployed yet / feature off). */
export const isUnavailable = (e: ApiError | null) =>
  !!e && (e.status === 404 || e.status === 501 || (e.status === 409 && e.code === "not_configured"));

/** Re-fetches every `ms` while the tab is visible. */
export function usePolling(reload: () => unknown, ms: number) {
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === "visible") void reload();
    }, ms);
    return () => window.clearInterval(t);
  }, [reload, ms]);
}

/** Today's UK date and the Monday of this week (YYYY-MM-DD). */
export function thisWeek(): { monday: string; today: string } {
  const today = isoLocalDate();
  const wd = weekdayOf(today); // 0 = Sunday
  return { monday: addDays(today, -((wd + 6) % 7)), today };
}

/** "Mon 3 Nov" style label for a YYYY-MM-DD date (rendered at UK midday to avoid DST edges). */
export function fmtIsoDate(date: string, opts: Intl.DateTimeFormatOptions = {}): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
    ...opts,
  }).format(Date.UTC(y, m - 1, d, 12));
}

/** dd/mm/yyyy for a YYYY-MM-DD date. */
export function ukIsoDate(date: string): string {
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

export function PageHeader({
  title,
  sub,
  back,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  back?: { to: string; label: string };
  actions?: ReactNode;
}) {
  return (
    <>
      {back && (
        <Link to={back.to} className="adm-back">
          ← {back.label}
        </Link>
      )}
      <div className="adm-head">
        <div>
          <h1>{title}</h1>
          {sub && <div className="sub">{sub}</div>}
        </div>
        {actions && <div className="row">{actions}</div>}
      </div>
    </>
  );
}

/** A labelled checkbox. */
export function Check({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="adm-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export interface RadioOption<T extends string> {
  value: T;
  title: string;
  description: ReactNode;
}

/** Radio buttons rendered as cards with a one-sentence explanation each. */
export function RadioCards<T extends string>({
  legend,
  hint,
  name,
  value,
  options,
  onChange,
  columns = false,
}: {
  legend: string;
  hint?: ReactNode;
  name: string;
  value: T;
  options: RadioOption<T>[];
  onChange: (v: T) => void;
  columns?: boolean;
}) {
  return (
    <fieldset className="adm-fieldset">
      <legend>{legend}</legend>
      {hint && <div className="hint">{hint}</div>}
      <div className={`adm-radios${columns ? " cols" : ""}`}>
        {options.map((o) => (
          <label key={o.value} className="adm-radio">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>
              <span className="t">{o.title}</span>
              <br />
              <span className="d">{o.description}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Dialog that collects a reason and runs an action. Shows the server's message on failure
 * and stays open so the admin can fix and retry.
 */
export function ReasonDialog({
  title,
  intro,
  label = "Reason",
  placeholder,
  confirmLabel,
  danger = false,
  required = true,
  onConfirm,
  onClose,
}: {
  title: string;
  intro?: ReactNode;
  label?: string;
  placeholder?: string;
  confirmLabel: string;
  danger?: boolean;
  required?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (required && !reason.trim()) {
      setError("Please give a reason.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  }

  return (
    <Dialog title={title} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        {intro}
        <Field label={required ? label : `${label} (optional)`}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder={placeholder}
            autoFocus
          />
        </Field>
        {error && <div className="banner danger">{error}</div>}
        <div className="row">
          <button type="submit" className={`btn ${danger ? "danger" : "primary"}`} disabled={busy}>
            {busy ? "Saving…" : confirmLabel}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Simple yes/no confirmation that runs an async action. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  danger = false,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  }
  return (
    <Dialog title={title} onClose={onClose}>
      {children}
      {error && <div className="banner danger">{error}</div>}
      <div className="row">
        <button className={`btn ${danger ? "danger" : "primary"}`} onClick={go} disabled={busy}>
          {busy ? "Working…" : confirmLabel}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

/** Shows a loading line, an error, or the children once data has arrived. */
export function LoadState<T>({
  state,
  children,
  unavailable,
}: {
  state: Loadable<T>;
  children: (data: T) => ReactNode;
  /** Message when the endpoint doesn't exist yet. */
  unavailable?: ReactNode;
}) {
  if (state.error && isUnavailable(state.error) && unavailable) {
    return <div className="banner info">{unavailable}</div>;
  }
  if (state.error && state.data === undefined) {
    return (
      <div className="banner danger" role="alert">
        <div className="row between">
          <span>{state.error.message}</span>
          <button className="btn sm" onClick={() => void state.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (state.data === undefined) return <p className="muted">Loading…</p>;
  return <>{children(state.data)}</>;
}

const OCCURRENCE_BADGE: Record<OccurrenceStatus, [string, string]> = {
  upcoming: ["", "Upcoming"],
  due: ["info", "Due now"],
  late: ["warn", "Late"],
  clocked_in: ["info", "Clocked in"],
  done: ["ok", "Done"],
  missed: ["danger", "Missed"],
};

export function OccurrenceBadge({ status }: { status: OccurrenceStatus }) {
  const [kind, label] = OCCURRENCE_BADGE[status] ?? ["", status];
  return <span className={`badge ${kind}`}>{label}</span>;
}

/** Human labels for audit actions; unknown actions fall back to the raw code. */
const ACTION_LABELS: Record<string, string> = {
  "visit.clock_in": "Clocked in",
  "visit.clock_out": "Clocked out",
  "visit.edit": "Visit edited",
  "visit.manual_create": "Visit added by admin",
  "visit.approve": "Approved",
  "visit.reject": "Rejected",
  "visit.missing_clock_out": "Marked missing clock-out",
  "punch.blocked": "Punch blocked",
  "punch.rejected": "Punch rejected",
  "site.create": "Site added",
  "site.update": "Site updated",
  "site.qr_regenerate": "QR code regenerated",
  "site.delete": "Site deleted",
  "stock.request": "Stock requested",
  "stock.cancel": "Stock request cancelled",
  "stock.done": "Stock request sorted",
  "stock.check_ok": "Friday stock check: nothing needed",
  "stock.item_create": "Stock item added",
  "stock.item_update": "Stock item changed",
  "person.create": "Person added",
  "person.update": "Person updated",
  "person.sites": "Assigned sites changed",
  "settings.update": "Settings changed",
  "export.create": "Export created",
  "export.void": "Export voided",
  "schedule.create": "Schedule added",
  "schedule.update": "Schedule changed",
  "schedule.delete": "Schedule deleted",
  "quickbooks.connect": "QuickBooks connected",
  "quickbooks.disconnect": "QuickBooks disconnected",
  "quickbooks.sync": "Sent to QuickBooks",
  "retention.run": "Retention clean-up",
};

export const actionLabel = (a: string) => ACTION_LABELS[a] ?? a;

function JsonCell({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null;
  return (
    <details className="adm-json">
      <summary>{label}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

const ENTITY_LINKS: Record<string, (id: string) => string> = {
  visit: (id) => `/admin/visits/${id}`,
  site: (id) => `/admin/sites/${id}`,
  person: (id) => `/admin/people/${id}`,
};

/** Audit rows as a table. `showEntity` hides the entity column on per-record views. */
export function AuditTable({ events, showEntity = true }: { events: AuditEvent[]; showEntity?: boolean }) {
  if (!events.length) return <Empty>No changes recorded.</Empty>;
  return (
    <div className="table-wrap">
      <table className="adm-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>What</th>
            {showEntity && <th>Record</th>}
            <th>Reason</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const link = e.entityId ? ENTITY_LINKS[e.entity]?.(e.entityId) : undefined;
            return (
              <tr key={e.id}>
                <td className="nowrap">{fmtDateTime(e.at)}</td>
                <td>{e.actorName ?? (e.actorId ? e.actorId : <span className="muted">System</span>)}</td>
                <td>
                  {actionLabel(e.action)}
                  <div className="muted small adm-code">{e.action}</div>
                </td>
                {showEntity && (
                  <td>
                    {e.entity}
                    {e.entityId && (
                      <div className="small">
                        {link ? (
                          <Link to={link} className="adm-code">
                            {e.entityId}
                          </Link>
                        ) : (
                          <span className="adm-code muted">{e.entityId}</span>
                        )}
                      </div>
                    )}
                  </td>
                )}
                <td>{e.reason ?? <span className="muted">—</span>}</td>
                <td>
                  <JsonCell label="Before" value={e.before} />
                  <JsonCell label="After" value={e.after} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Lookup helpers for id → name. */
export function nameMap<T extends { id: string; name: string }>(items: T[] | undefined): Map<string, string> {
  return new Map((items ?? []).map((i) => [i.id, i.name]));
}

/** Saves a string as a downloaded file. */
export function downloadText(filename: string, text: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Returns only the keys of `next` whose values differ from `prev` (shallow, JSON compare). */
export function changedFields<T extends object>(prev: T, next: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(next) as (keyof T)[]) {
    if (JSON.stringify(prev[k]) !== JSON.stringify(next[k])) out[k] = next[k];
  }
  return out;
}

/** Empty strings from text inputs become null for the API. */
export const orNull = (s: string) => (s.trim() ? s.trim() : null);

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Form state seeded from a saved record (a person, a site). When the record object changes
 * because something else on the page saved or reloaded it, the form follows the new record
 * only if it is a different record (another id) or the form has no unsaved edits. So saving
 * one form never silently throws away what was typed into another.
 */
export function useRecordForm<R, F>(
  record: R,
  recordKey: string,
  toForm: (r: R) => F,
): [F, Dispatch<SetStateAction<F>>] {
  const [form, setForm] = useState(() => toForm(record));
  const toFormRef = useRef(toForm);
  toFormRef.current = toForm;
  const base = useRef({ record, recordKey });
  useEffect(() => {
    const prev = base.current;
    if (prev.record === record && prev.recordKey === recordKey) return;
    base.current = { record, recordKey };
    const next = toFormRef.current;
    setForm((f) => (prev.recordKey !== recordKey || sameJson(f, next(prev.record)) ? next(record) : f));
  }, [record, recordKey]);
  return [form, setForm];
}
